import { Component, Input, OnChanges, OnDestroy, SimpleChanges, NgZone } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import * as am5index from '@amcharts/amcharts5/index';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5exporting from '@amcharts/amcharts5/plugins/exporting';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';

@Component({
	selector: 'app-progress-chart',
	standalone: true,
	imports: [CommonModule, MatButtonModule, TranslateModule, MatIconModule],

    template: `<div [id]="chartId" style="width: 100%; height: 300px;"></div>
      <div class="d-flex justify-content-end align-items-center mb-4">
        <button class="filters-show-button " type="button" mat-raised-button color="primary" aria-label="Export chart button" (click)="exportAsImage()">
        <mat-icon class="m-0">download</mat-icon>
        </button>
      </div>`,
	styleUrl: './progress-chart.component.scss',
})
export class ProgressChartComponent implements OnChanges, OnDestroy {
  chartId = `progressChartDiv-${Math.random().toString(36).substr(2, 9)}`;
	@Input() data: { sentimentType?: string; clipsCount?: number }[] = [];

	private root!: am5.Root;
	private exporting!: am5exporting.Exporting;

	constructor(private zone: NgZone, private translate: TranslateService) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (this.data?.length) {
			this.renderChart();
		}
	}

	renderChart(): void {
		this.disposeChart();

		this.zone.runOutsideAngular(() => {
			const root = am5index.Root.new(this.chartId);
			this.root = root;

			root.setThemes([am5themes_Animated.new(root)]);

			const chart = root.container.children.push(
				am5xy.XYChart.new(root, {
					panX: false,
					panY: false,
					layout: root.verticalLayout,
				})
			);

			const yAxis = chart.yAxes.push(
				am5xy.CategoryAxis.new(root, {
					categoryField: 'category',
					renderer: am5xy.AxisRendererY.new(root, {}),
				})
			);
			yAxis.data.setAll([{ category: '' }]);

			const xAxis = chart.xAxes.push(
				am5xy.ValueAxis.new(root, {
					min: 0,
					max: 100,
					numberFormat: "#'%'",
					renderer: am5xy.AxisRendererX.new(root, {}),
				})
			);
			xAxis.get('renderer').labels.template.set('forceHidden', true);

			const series = chart.series.push(
				am5xy.ColumnSeries.new(root, {
					xAxis,
					yAxis,
					valueXField: 'to',
					openValueXField: 'from',
					categoryYField: 'category',
					categoryXField: 'name',
				})
			);

			series.columns.template.setAll({
				strokeWidth: 0,
				strokeOpacity: 0,
				height: am5.percent(100),
				templateField: 'columnSettings',
			});

			// Generate percent data
			const total = this.data.reduce((sum, item) => sum + (item.clipsCount ? item.clipsCount : 0), 0);
			let from = 0;

			const colors: Record<string, number> = {
				POSITIVE: 0x0ca948,
				NEUTRAL: 0xffd100,
				NEGATIVE: 0xcd213b,
			};

			const formattedData = this.data.map(item => {
        const clips = item.clipsCount ?? 0;
        const to = from + (clips / total) * 100;
        const sentimentKey = item.sentimentType ?? 'POSITIVE';
        const translatedName = this.translate.instant(`CLIPS.SENTIMENT_RATING_TYPE.${sentimentKey}`);

        const segment = {
          category: '',
          from,
          to,
          name: translatedName,
          columnSettings: {
            fill: am5.color(colors[sentimentKey] || 0x9e9e9e),
          },
        };

        from = to;
        return segment;
      });

			series.data.setAll(formattedData);

			// Add range labels
			formattedData.forEach(d => {
				const rangeDataItem = xAxis.makeDataItem({ value: d.from });
				const range = xAxis.createAxisRange(rangeDataItem);
				rangeDataItem.get('grid')?.set('forceHidden', true);
				rangeDataItem.get('tick')?.setAll({ visible: true, length: 18, strokeOpacity: 0.2 });
				rangeDataItem.get('label')?.setAll({ centerX: am5.p0, forceHidden: false, fontSize: 10, text: `${Math.round(d.from)}%` });
			});

			const legend = chart.children.push(
				am5.Legend.new(root, {
					nameField: 'categoryX',
					centerX: am5.percent(50),
					x: am5.percent(50),
					clickTarget: 'none',
				})
			);
			legend.markerRectangles.template.setAll({
				strokeOpacity: 0,
			});
			legend.data.setAll(series.dataItems);

			series.appear();
			chart.appear(1000, 100);

			this.exporting = am5exporting.Exporting.new(root, {
				pngOptions: {
					quality: 0.8,
					maintainPixelRatio: true,
				},
			});
		});
	}

	exportAsImage() {
		this.exporting?.download('png');
	}

	ngOnDestroy(): void {
		this.disposeChart();
	}

	private disposeChart() {
		if (this.root) {
			this.root.dispose();
		}
	}
}
